import React, {useEffect, useCallback} from 'react';
import sAction from 'sAction';
import Loader from 'ROOT/src/components/loader';
import PropTypes from 'prop-types';

export default function MicrosoftSSO(props) {
    /**
     * Remove redirect data from microsoft redirect url
     */
    const clearUrl = () => {
        window.history.pushState({}, document.title, window.location.pathname);
    };

    /**
     * parseUrlParams() - parse url params
     * @param {string} url
     * @returns {object} parsed url object
     */
    const parseUrlParams = (url) => {
        const params = url.split('?')[1];
        const urlParams = new URLSearchParams(params);
        return urlParams;
    };

    const forcedSSOSetup = useCallback((code, params, token) => {
        /** @see src/components/login/SSOSetupLogin.js */
        sAction.removeStorage('SETUP_ENFORCED_MICROSOFT_SSO_LOGIN');
        // we are coming from SSO setup page, we have token, we need pair user and email together and create session
        sAction.load();
        sAction.rest.fetchData('Office365/afterSSOForcedSetup', 'POST', {
            code: code,
            token: token,
            language: sAction.getLanguage(),
        }).then((loginResponse) => {
            clearUrl();
            setTimeout(() => {
                sAction.rest.setCookie('sID', loginResponse.data.sid, 1);
                sAction.initData(loginResponse.data);
                sAction.dsProcess();
                sAction.checkNewNotifications();
                const view = sAction.getViewName();
                sAction.route(true, null, view === 'login' ? 'home' : null);
            }, 1500);
        }).catch((errorResponse) => {
            console.error('Clearing after SSO setup failed', /*code, token, params, */ errorResponse);
            clearUrl();
            sAction.error(sAction.translate(errorResponse?.text || errorResponse?.error?.message || 'Err78914253645'));
        }).finally(() => {
            sAction.unLoad();
        });
    }, []);

    const loginWithSSO = useCallback((code, params) => {
        const requestBody = {
            code: code,
            language: sAction.getLanguage(),
            urlParams: params,
        };
        sAction.rest.fetchData('microsoftSSO', 'POST', requestBody).then((response) => {
            const data = response.data;
            sAction.rest.setCookie('sID', data.sid, 1);
            clearUrl();
            sAction.initData(data);
            if (sAction.getViewName() === 'login') {
                sAction.illegallyChangeHash('#home');
            }
            sAction.dsProcess();
            sAction.route(true, data);
            sAction.checkNewNotifications();
        }).catch((errorResponse) => {
            sAction.error(sAction.translate(errorResponse.text));
            clearUrl();
            console.error('Clearing after SSO', code, params, errorResponse);
            props.goToLogin();
        }).finally(() => {
            // On localhost we have different ports => different sites => different local storages,
            // which is set on one port is not set on another!!!

            // When we are logging from another device type (mobile/servis) we are redirected to desktop version
            // because we don't want to setup redirect uri for each device
            const redirectMobile = sAction.getStorage('SSO_REDIRECT_TO_MOBILE');
            const redirectServis = sAction.getStorage('SSO_REDIRECT_TO_SERVIS');
            switch (true) {
                case redirectMobile:
                    sAction.redirectToView('mobile');
                    break;
                case redirectServis:
                    sAction.redirectToView('servis');
                    break;
            }
        });
    }, []);

    useEffect(() => {
        const urlParams = parseUrlParams(window.location.hash);
        const code = urlParams.get('code');
        if (!code) {
            sAction.error('LBL_NO_CODE');
        }
        const params = {};
        urlParams.forEach((item, key) => {
            params[key] = item;
        });
        params['__CORIPO_REDIRECT_LOCATION'] = window.location;

        const token = sAction.getStorage('SETUP_ENFORCED_MICROSOFT_SSO_LOGIN');
        if (token && code) {
            forcedSSOSetup(code, params, token);
            return;
        }
        loginWithSSO(code, params);
    }, []);

    return <div>
        <Loader></Loader>
    </div>;
}

MicrosoftSSO.propTypes = {
    goToLogin: PropTypes.func.isRequired,
};
